/* eslint-disable react/no-multi-comp */
/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Stack,
  CircularProgress,
  Grid,
  Tooltip,
  Backdrop,
} from '@mui/material';
import {
  Button,
  Combobox,
  FormHelperText,
  Typography,
} from 'pvs-design-system';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Notify } from 'components/Notifications/Notifications';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ROUTES } from 'constants/constants';
import {
  useDownloadToolTemplateMutation,
  useBulkImportLinksValidationMutation,
  useBulkInsertLinksMutation,
  useDownloadImportErrorFileMutation,
} from 'api';
import ReactMarkdown from 'react-markdown';
import { GetToolsDto } from 'types';

interface MultipleLinksFormProps {
  tools: GetToolsDto[];
}

const MultipleLinksForm: React.FC<MultipleLinksFormProps> = ({ tools }) => {
  const navigate = useNavigate();
  const [toolId, setToolId] = useState<string | null>();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [successFileName, setSuccessFileName] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<number>(0);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const selectedToolOption = useMemo(
    () =>
      tools
        .map((tool) => ({ label: tool.id, value: tool.id }))
        .find((opt) => opt.value === toolId) || null,
    [tools, toolId]
  );

  const [downloadTemplate] = useDownloadToolTemplateMutation();
  const [bulkInsertLinks] = useBulkInsertLinksMutation();
  const [bulkImportToolLinksValidation] =
    useBulkImportLinksValidationMutation();
  const [downloadImportErrorFile] = useDownloadImportErrorFileMutation();

  const downloadErrorId = useMemo(() => {
    if (!error) return null;
    const match = error.match(/download-error-file:\/\/(\d+)/);
    return match ? match[1] : null;
  }, [error]);

  const processFile = async (inputFile: File | null) => {
    if (!inputFile) return;

    if (!toolId) {
      Notify.error('Please select a tool first before uploading a file.');
      return;
    }

    // Validation: size(10MB)
    if (inputFile.size > 10 * 1024 * 1024) {
      setError(
        '**File Size Limit Exceeded** - The file could not be uploaded because it exceeds the maximum allowed size.\nPlease upload a smaller file and try again.'
      );
      setFile(null);
      return;
    }

    // Validation: extension
    if (!/\.(csv|xlsx)$/i.test(inputFile.name)) {
      setError(
        '**Invalid File Format** – The file could not be uploaded due to an unsupported format.\nPlease upload a file in a supported format (e.g., .csv, .xlsx) and try again.'
      );
      setFile(null);
      return;
    }

    try {
      setError(null);
      setFile(inputFile);
      setUploadProgress(0);

      // ⏳ Fake progress simulation while waiting for API
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev < 90) {
            return prev + 10;
          }
          return prev;
        });
      }, 500);

      // Call mutation
      const result = await bulkImportToolLinksValidation({
        toolId,
        file: inputFile,
      }).unwrap();
      setSuccessFileName(result.successFileName);
      setSessionId(result.sessionId);

      clearInterval(interval);
      setUploadProgress(100);
    } catch (err: unknown) {
      setUploadProgress(100);

      if (typeof err === 'object' && err !== null && 'data' in err) {
        const errorData = err as { data?: Record<string, unknown> };
        setError(errorData.data?.errorMessage as string);
        setSessionId(errorData.data?.sessionId as number);
        setSuccessFileName(errorData.data?.successFileName as string);
      } else {
        setError('An unknown error occurred.');
      }
    } finally {
      setUploadProgress(100);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFile = event.dataTransfer.files?.[0] || null;
    processFile(droppedFile);
  };

  const handleAddLinks = async () => {
    try {
      if (!toolId || !file) {
        setError('Please upload a tool to add links.');
        return;
      }
      setShowBackdrop(true);

      await bulkInsertLinks({
        toolId,
        fileName: successFileName,
        sessionId,
      }).unwrap();

      navigate(ROUTES.links, {
        state: {
          toastMessage:
            'Import started – your links are being processed and will be added shortly.',
        },
      });
    } catch (err: unknown) {
      const errorData = err as { data?: { message?: string } };
      const errorMessage =
        errorData?.data?.message ||
        'The link import was not successful. Check the error details file in the Import Log for more information.';

      navigate(ROUTES.importLogs, {
        state: {
          toastMessage: errorMessage,
          toastType: 'error',
        },
      });
    } finally {
      setShowBackdrop(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0] || null;
    processFile(uploadedFile);
  };

  const handleDeleteFile = () => {
    setFile(null);
    setUploadProgress(0);
    setError(null);
  };

  const handleDownloadTemplate = async () => {
    if (!toolId) {
      Notify.error('Please select a Tool first.');
      return;
    }
    try {
      await downloadTemplate(toolId).unwrap();
      Notify.success('Template downloaded successfully.');
    } catch {
      Notify.error('Failed to download template.');
    }
  };

  // Determine border and background based on file state
  let borderStyle = '2px dashed #7a226f';
  let backgroundStyle = 'transparent';
  let minHeightStyle = 160;

  if (file && uploadProgress >= 100) {
    borderStyle = '1px solid #ccc';
    backgroundStyle = '#fff';
    minHeightStyle = 56;
  }

  // Determine upload state display
  let uploadContent;
  if (!file) {
    uploadContent = (
      <>
        <input
          type="file"
          id="fileUpload"
          accept=".csv, .xlsx"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Box
          component="label"
          htmlFor="fileUpload"
          sx={{ cursor: 'pointer', width: '100%' }}
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            <UploadFileIcon
              sx={{ fontSize: 40, color: 'primary.main', mr: 1 }}
            />
            <Typography
              variant="h5"
              sx={{ mt: 0.5, color: 'primary.main', fontWeight: 600 }}
            >
              Browse or drop a file to upload
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              width: '100%',
              textTransform: 'none',
              fontWeight: 'normal',
              mt: 1,
            }}
          >
            XLSX or CSV (Max size: 10MB)
          </Typography>
        </Box>
      </>
    );
  } else if (uploadProgress < 100) {
    uploadContent = (
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Box position="relative" display="inline-flex" mb={2}>
          <CircularProgress
            variant="determinate"
            value={uploadProgress}
            size={48}
            thickness={4}
            sx={{ color: '#7a226f' }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{ color: '#7a226f', fontWeight: 700 }}
            >
              {`${uploadProgress}%`}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: '#7a226f', mb: 0.5 }}
        >
          Uploading File
        </Typography>
        <Typography variant="h5" sx={{ color: '#222' }}>
          {file.name}
        </Typography>
      </Box>
    );
  } else {
    uploadContent = (
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minHeight: 48 }}
      >
        <Box display="flex" alignItems="center">
          <InsertDriveFileIcon sx={{ fontSize: 24, color: '#666666', mr: 1 }} />
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography
              variant="h1"
              sx={{
                fontWeight: 400,
                color: '#222',
                lineHeight: '20px',
                fontSize: '14px',
                fontFamily: 'Open Sans, Arial, sans-serif',
                letterSpacing: 0,
              }}
            >
              {file.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontWeight: 400,
                lineHeight: '20px',
                fontSize: '12px',
                fontFamily: 'Open Sans, Arial, sans-serif',
              }}
            >
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </Typography>
          </Box>
        </Box>
        <Box
          component="button"
          id="delete-file-button"
          sx={{
            background: '#7a226f',
            borderRadius: '50%',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            ml: 1,
            border: 'none',
            transition: 'background 0.2s',
            '&:hover': {
              background: '#5a1852',
            },
          }}
          onClick={handleDeleteFile}
        >
          <CloseIcon
            sx={{
              color: '#fff !important',
              fontSize: 16,
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, margin: '0 auto', px: 2, mt: 8 }}>
      <Grid
        container
        alignItems="center"
        spacing={2}
        sx={{
          height: 92,
          opacity: 1,
        }}
      >
        <Grid
          item
          xs={12}
          md={9}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Typography
            variant="h2"
            component="span"
            fontWeight={700}
            sx={{
              mb: 1,
              fontSize: '16px',
              fontFamily: 'Open Sans, Arial, sans-serif',
            }}
          >
            Tool <span style={{ color: '#ff0000' }}>*</span>
          </Typography>
          <Combobox
            options={tools.map((tool) => ({
              label: tool.id,
              value: tool.id,
            }))}
            value={selectedToolOption} // ✅ keeps selection visible
            onChange={(
              event,
              newValue: { label: string; value: string } | null
            ) => setToolId(newValue?.value || null)}
            textFieldProps={{
              id: 'tool-combobox',
              placeholder: 'Choose item',
              sx: {
                width: '100%',
                height: 40,
                borderRadius: '6px',
                borderWidth: '1px',
              },
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Open Sans, Arial, sans-serif',
              fontWeight: 100,
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: 0,
              mt: 0.125,
              height: '40px',
              color: 'text.secondary',
            }}
          >
            Select a vendor to get the correct template for bulk link import.
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
          display="flex"
          alignItems="center"
          justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          sx={{ height: '100%' }}
        >
          <Tooltip title="Please choose a vendor from the dropdown first to download the template.">
            <span>
              <Button
                id="download-template-button"
                variant="contained"
                color="primary"
                onClick={handleDownloadTemplate}
                disabled={!toolId}
                startIcon={<DownloadIcon />}
                sx={{
                  height: 40,
                  minWidth: 220,
                  fontWeight: 700,
                  fontSize: '16px',
                  fontFamily: 'Open Sans, Arial, sans-serif',
                  textTransform: 'none',
                  backgroundColor: '#7a226f',
                  borderRadius: 2,
                  mt: '6px',
                  '&:hover': { backgroundColor: '#5a1852' },
                }}
              >
                Download Template
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <Typography
        variant="h2"
        component="span"
        sx={{
          fontFamily: 'Open Sans, Arial, sans-serif',
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: 0,
          mb: 1,
          mt: 2,
          display: 'inline-block',
        }}
      >
        Upload Completed Template <span style={{ color: '#ff0000' }}>*</span>
      </Typography>

      <Box
        mt={1}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          border: borderStyle,
          background: backgroundStyle,
          borderRadius: 2,
          minHeight: minHeightStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: file && uploadProgress >= 100 ? 1 : 0,
          py: file && uploadProgress >= 100 ? 0.5 : 3,
          boxShadow: file && uploadProgress >= 100 ? 'none' : undefined,
          textAlign: 'left',
          transition: 'border 0.2s, background 0.2s',
        }}
      >
        {uploadContent}
      </Box>

      {error && (
        <Box
          mt={2}
          p={2}
          sx={{
            backgroundColor: '#fdecea',
            border: '1px solid #E22C2C',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <ErrorOutlineIcon color="error" sx={{ mr: 1, mt: 0.5 }} />
          <ReactMarkdown
            components={{
              strong: ({ children }) => (
                <Typography
                  variant="h2"
                  component="span"
                  fontWeight="bold"
                  fontSize="14px"
                  color="#000"
                  sx={{ fontFamily: 'Open Sans, Arial, sans-serif' }}
                >
                  {children}
                </Typography>
              ),
              p: ({ children }) => (
                <Typography
                  variant="h1"
                  component="span"
                  fontSize="14px"
                  fontWeight="normal"
                  color="#000"
                  sx={{ fontFamily: 'Open Sans, Arial, sans-serif' }}
                >
                  {children}
                </Typography>
              ),
              a: ({ href, children }) => {
                if (!href && downloadErrorId) {
                  return (
                    <Box
                      component="button"
                      id="download-error-file-button"
                      onClick={async () => {
                        Notify.info('Your file is being downloaded…');
                        try {
                          await downloadImportErrorFile(downloadErrorId);
                        } catch {
                          Notify.error(
                            'Failed to download error details file.'
                          );
                        }
                      }}
                      sx={{
                        color: '#7a226f',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        cursor: 'pointer',
                        display: 'inline',
                        '&:hover': {
                          color: '#5a1852',
                        },
                      }}
                    >
                      {children}
                    </Box>
                  );
                }
                return (
                  <a
                    id="error-details-link"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#7a226f' }}
                  >
                    {children}
                  </a>
                );
              },
            }}
          >
            {error}
          </ReactMarkdown>
        </Box>
      )}

      <Typography
        variant="h2"
        sx={{
          fontFamily: 'Open Sans, Arial, sans-serif',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: 0,
          mt: 1,
          color: 'text.secondary',
        }}
      >
        Upload your completed template file containing all link details for bulk
        import.
      </Typography>
      <FormHelperText
        id="requiredIdText"
        helperText="* Indicates a required field"
        sx={{ color: '#ff0000', mt: 2 }}
      />

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        mt={3}
      >
        <RouterLink id="cancel-link" to={ROUTES.links}>
          <Button
            id="cancel-button"
            color="secondary"
            onClick={() => navigate(ROUTES.links)}
          >
            Cancel
          </Button>
        </RouterLink>

        <Tooltip
          title={
            !file || uploadProgress < 100
              ? 'Please wait for the upload to complete.'
              : 'Add links'
          }
        >
          <span>
            <Button
              id="add-links-button"
              color="primary"
              disabled={!file || uploadProgress < 100}
              onClick={handleAddLinks}
            >
              Add Links
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
        <Typography
          variant="h2"
          sx={{
            ml: 2,
            fontWeight: 600,
            fontFamily: 'Open Sans, Arial, sans-serif',
          }}
        >
          Uploading
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default MultipleLinksForm;
